import { useMemo, useState } from "react";
import { hotStreamers, rankingPeriods } from "@/mocks/home";
import SectionHeading from "@/pages/home/components/SectionHeading";

const periodFactor: Record<string, number> = {
  今日榜: 1,
  本周榜: 0.97,
  本月榜: 1.03,
};

export default function RankingSection() {
  const [period, setPeriod] = useState(rankingPeriods[0]);

  const rows = useMemo(() => {
    const factor = periodFactor[period] ?? 1;
    return hotStreamers
      .map((item) => {
        const value = Number(item.score) * factor;
        return { ...item, displayScore: value.toFixed(1) };
      })
      .sort((a, b) => Number(b.displayScore) - Number(a.displayScore))
      .map((item, index) => ({ ...item, displayRank: index + 1 }));
  }, [period]);

  const rankStyle = (rank: number) => {
    if (rank === 1) return "bg-primary-500 text-background-50";
    if (rank === 2) return "bg-accent-500 text-background-50";
    if (rank === 3) return "bg-secondary-500 text-background-50";
    return "bg-background-200 text-foreground-700";
  };

  return (
    <section id="ranking" className="py-16 md:py-24">
      <div className="mx-auto max-w-page px-4 md:px-6">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between mb-8 md:mb-12">
          <SectionHeading
            eyebrow="Leaderboard"
            title="51视频热门主播榜"
            desc="榜单综合观看量、互动量与更新频率计算，按今日、本周、本月三个周期分别统计。数值代表当前统计周期内的综合热度，仅供浏览参考。"
          />
          <div
            className="inline-flex shrink-0 rounded-full bg-secondary-100 p-1 gap-1"
            role="tablist"
            aria-label="榜单周期"
          >
            {rankingPeriods.map((item) => {
              const active = item === period;
              return (
                <button
                  key={item}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setPeriod(item)}
                  className={`rounded-full px-4 py-1.5 text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    active
                      ? "bg-primary-500 text-background-50 font-medium"
                      : "text-secondary-900 hover:text-primary-700"
                  }`}
                >
                  {item}
                </button>
              );
            })}
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-background-200" data-reveal>
          <div className="hidden md:grid grid-cols-12 gap-4 bg-background-200 px-5 py-3 text-xs font-medium text-foreground-600">
            <span className="col-span-1">排名</span>
            <span className="col-span-4">主播</span>
            <span className="col-span-2">分类</span>
            <span className="col-span-2 text-right">热度值</span>
            <span className="col-span-2 text-right">粉丝数</span>
            <span className="col-span-1 text-right">涨粉</span>
          </div>

          <ul className="divide-y divide-background-200 bg-background-50">
            {rows.map((item) => (
              <li
                key={item.id}
                className="grid grid-cols-12 items-center gap-3 md:gap-4 px-4 md:px-5 py-4 transition-colors hover:bg-primary-50/60"
              >
                <div className="col-span-2 md:col-span-1 flex items-center gap-2">
                  <span
                    className={`w-7 h-7 flex items-center justify-center rounded-md font-num text-xs font-bold ${rankStyle(
                      item.displayRank,
                    )}`}
                  >
                    {item.displayRank}
                  </span>
                </div>
                <div className="col-span-10 md:col-span-4 flex items-center gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-200 text-sm font-medium text-secondary-900 shrink-0">
                    {item.name.slice(0, 1)}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground-950 truncate">
                      {item.name}
                    </p>
                    <p className="text-[11px] text-foreground-500 truncate">{item.tag}</p>
                  </div>
                </div>
                <div className="hidden md:block md:col-span-2">
                  <span className="rounded-full bg-accent-100 px-2.5 py-1 text-[11px] font-medium text-accent-800">
                    {item.category}
                  </span>
                </div>
                <div className="col-span-6 md:col-span-2 text-left md:text-right">
                  <span className="font-num text-sm font-bold text-primary-600">
                    {item.displayScore}
                  </span>
                  <span className="md:hidden ml-1 text-[11px] text-foreground-500">热度</span>
                </div>
                <div className="col-span-3 md:col-span-2 text-right font-num text-xs md:text-sm text-foreground-700">
                  {item.fans}
                </div>
                <div className="col-span-3 md:col-span-1 text-right">
                  <span className="font-num text-xs md:text-sm text-accent-700">
                    {item.growth}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <p className="mt-4 text-xs text-foreground-500" data-reveal>
          榜单每 6 小时更新一次，最近一次更新于 {period} 数据统计完成后。
        </p>
      </div>
    </section>
  );
}